import type { AnyFieldApi } from "@tanstack/solid-form";
import { AiFillCheckCircle, AiOutlinePlusCircle } from "solid-icons/ai";
import { IoClose } from "solid-icons/io";
import {
  type Component,
  createMemo,
  createSignal,
  For,
  Index,
  type JSX,
  Show,
  splitProps,
} from "solid-js";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type TagInputProps = {
  fieldApi: AnyFieldApi;
  disabled?: boolean;
  placeholder?: string;
  maxTags?: number;
  maxTagLength?: number;
} & JSX.HTMLAttributes<HTMLDivElement>;

const normalizeTags = (tag: string): string[] => {
  return tag
    .split(/[,;]/)
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t !== "");
};

export const TagInput: Component<TagInputProps> = (props) => {
  const [localProps, restProps] = splitProps(props, [
    "fieldApi",
    "class",
    "disabled",
    "placeholder",
    "maxTags",
    "maxTagLength",
  ]);
  const [inputValue, setInputValue] = createSignal("");

  const tags = createMemo<string[]>(() => {
    const value = localProps.fieldApi.state.value;
    if (!value || !Array.isArray(value)) return [];
    return value as string[];
  });

  const addTags = (raw: string) => {
    const incoming = normalizeTags(raw);
    if (incoming.length === 0) return;

    const current = tags();
    const available = Math.max(
      0,
      (localProps.maxTags ?? Number.POSITIVE_INFINITY) - current.length,
    );
    const unique = incoming
      .filter(
        (tag) =>
          !current.includes(tag) &&
          tag.length <= (localProps.maxTagLength ?? Number.POSITIVE_INFINITY),
      )
      .slice(0, available);
    if (unique.length > 0) {
      for (const tag of unique) {
        localProps.fieldApi.pushValue(tag);
      }
    }
    setInputValue("");
  };

  const removeTag = (index: number) => {
    localProps.fieldApi.removeValue(index);
  };

  return (
    <div
      class={`flex items-center gap-4 flex-wrap border border-neutral-300 rounded-md p-2 w-full ${localProps.class ?? ""}`}
      {...restProps}
    >
      <div class="flex w-fit gap-1 flex-wrap shrink-0">
        <For each={tags()}>
          {(value, index) => (
            <Badge
              variant="secondary"
              size="sm"
              class="text-xs text-muted-foreground"
            >
              {value.toLowerCase()}
              <Button
                variant="ghost"
                size="icon"
                class="p-0 h-6 w-6"
                onClick={() => removeTag(index())}
                disabled={localProps.disabled}
              >
                <IoClose />
              </Button>
            </Badge>
          )}
        </For>
      </div>
      <Input
        placeholder={
          localProps.placeholder ?? "Premi invio per aggiungere un tag"
        }
        class="min-w-9 w-max bg-transparent border-none outline-hidden grow px-1"
        value={inputValue()}
        onInput={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const value = inputValue().trim();
            if (value !== "") {
              queueMicrotask(() => addTags(value));
            }
          }
        }}
        onBlur={(e) => {
          e.preventDefault();
          const value = inputValue().trim();
          if (value !== "") {
            queueMicrotask(() => addTags(value));
          }
          localProps.fieldApi.handleBlur();
        }}
        disabled={localProps.disabled}
      />
    </div>
  );
};

type TagInputSuggestionsProps = {
  fieldApi: AnyFieldApi;
  suggestions: string[];
  label?: string;
  maxTags?: number;
  maxTagLength?: number;
} & JSX.HTMLAttributes<HTMLDivElement>;

export const TagInputSuggestions: Component<TagInputSuggestionsProps> = (
  props,
) => {
  const [localProps, restProps] = splitProps(props, [
    "fieldApi",
    "suggestions",
    "label",
    "class",
    "maxTags",
    "maxTagLength",
  ]);

  const tags = createMemo<string[]>(() => {
    const value = localProps.fieldApi.state.value;
    if (!value || !Array.isArray(value)) return [];
    return value as string[];
  });

  const toggleTag = (skill: string) => {
    const current = tags();
    const normalized = skill.toLowerCase();
    if (current.includes(normalized)) {
      const index = current.indexOf(normalized);
      localProps.fieldApi.removeValue(index);
    } else {
      if (
        current.length >= (localProps.maxTags ?? Number.POSITIVE_INFINITY) ||
        normalized.length >
          (localProps.maxTagLength ?? Number.POSITIVE_INFINITY)
      ) {
        return;
      }
      localProps.fieldApi.pushValue(normalized);
    }
  };

  const selectedTagsSet = createMemo(() => new Set(tags()));
  const isSelected = (skill: string) =>
    selectedTagsSet().has(skill.toLowerCase());

  return (
    <div class={`flex flex-col gap-2 ${localProps.class ?? ""}`} {...restProps}>
      <Show when={localProps.label}>
        <p class="text-sm text-gray-500">{localProps.label}</p>
      </Show>
      <div class="flex items-center gap-2 flex-wrap">
        <Index each={localProps.suggestions}>
          {(skill) => (
            <Badge
              variant="outline"
              size="md"
              class="cursor-pointer bg-white"
              onClick={() => toggleTag(skill())}
              classList={{
                "border-primary text-primary": isSelected(skill()),
              }}
            >
              <Show
                when={isSelected(skill())}
                fallback={<AiOutlinePlusCircle class="w-4 h-4 mr-2" />}
              >
                <AiFillCheckCircle class="w-4 h-4 mr-2" />
              </Show>
              {skill()}
            </Badge>
          )}
        </Index>
      </div>
    </div>
  );
};
